import { useNavigate } from "react-router-dom";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { FormActionsNew, FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";
import { Diff } from "@/features/FormCard/Diff";
import { NewDiff } from "@/features/FormCard/NewDiff";
import {
  CancelConfirmationModal,
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { useEffect, useMemo, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { FixingMode } from "@/data/fixingMode";
import { computeFixingMode } from "../A5TimeSeries/utils";
import { useEditForm } from "@/features/Edit/hooks";

import {
  client,
  AssetOption,
  FormSchema,
  includeClientBankFields,
  names,
  portfolio_table_name,
  PortfolioOption,
  static_data_table_name,
  table_name,
  TFormValues,
} from "./config";

export function BankPositionsFormCard({ id }: { id?: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form: _form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    ...(id != null ? { id } : { isNew: true as const }),
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === names.portfolioName) {
        return {
          text: value,
          id: fetchedData[names.portfolioId],
          account_type: fetchedData[names.portfolioAccountType],
          currency: fetchedData[names.portfolioCurrency],
          reporting_currency: fetchedData[names.portfolioReportingCurrency],
        };
      }

      if (key === "client") {
        return {
          text: fetchedData[names.portfolioClient],
        };
      }

      if (key === "bank_name") {
        return {
          text: fetchedData[names.portfolioBankName],
        };
      }

      if (key === names.staticDatumName) {
        return {
          text: value,
          id: fetchedData[names.staticDatumId],
          currency: fetchedData[names.staticDatumCurrency],
          asset_class_2: fetchedData[names.staticDatumAssetClass2],
        };
      }

      return value;
    },
  });
  const form = _form as UseFormReturn<TFormValues>;

  const portfolio = form.watch(names.portfolioName) as PortfolioOption | null | undefined;
  const asset = form.watch(names.staticDatumName) as AssetOption | null | undefined;

  const portfolioId = portfolio?.id;
  const clientField = form.watch("client") as { text?: string } | null | undefined;
  const bankField = form.watch("bank_name") as { text?: string } | null | undefined;
  const clientName = clientField?.text ?? "";
  const bankName = bankField?.text ?? "";
  const localCurrency = asset?.currency ?? portfolio?.currency ?? "";
  const reportingCurrency = portfolio?.reporting_currency ?? "";
  const portfolioType = portfolio?.account_type;
  const assetClass2 = asset?.asset_class_2 ?? null;

  const isTypeSecurities = portfolioType === "securities";
  const fixingMode = useMemo(
    () => computeFixingMode(assetClass2 ?? undefined),
    [assetClass2],
  );
  const allowsFixingFields =
    isTypeSecurities && fixingMode === FixingMode.FIXING;

  const prevPortfolioIdRef = useRef<number | undefined>(undefined);
  const [portfolioJustCleared, setPortfolioJustCleared] = useState(false);

  useEffect(() => {
    const prevId = prevPortfolioIdRef.current;
    prevPortfolioIdRef.current = portfolioId;
    if (prevId && !portfolioId) {
      form.setValue("client", null, { shouldDirty: true });
      form.setValue("bank_name", null, { shouldDirty: true });
      setPortfolioJustCleared(true);
    } else if (portfolioId) {
      setPortfolioJustCleared(false);
    }
  }, [portfolioId, form]);

  useEffect(() => {
    if (allowsFixingFields) return;

    const fieldsToClear = ["fixing_local_ccy", "fixing_ptf_ccy", "quantity"] as const;

    fieldsToClear.forEach((fieldName) => {
      const value = form.getValues(fieldName);
      if (value != null) {
        form.setValue(fieldName, null, { shouldDirty: true });
      }
    });
  }, [allowsFixingFields, form]);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {id == null
                  ? t("bank_positions.new_bank_position")
                  : `${t("bank_positions.bank_position")} ${id}`}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t("bank_positions.date")}
                    control={form.control}
                    isRequired
                  />
                </div>
                <AutocompleteAsyncField
                  name={names.staticDatumName}
                  label={t("bank_positions.asset")}
                  control={form.control}
                  base_url={`${client}/${static_data_table_name}`}
                  id_column={names.staticDatumId}
                  column_name="name"
                  isRequired
                  options={{
                    translateKey: "asset_class_2",
                    client,
                    table_name: static_data_table_name,
                    language: "fr",
                  }}
                />
                {includeClientBankFields ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutocompleteAsyncField
                      name="client"
                      label={t("transactions.client")}
                      control={form.control}
                      base_url={`${client}/${portfolio_table_name}`}
                      id_column={names.portfolioId}
                      column_name="client"
                      showId={false}
                      isRequired
                      options={{
                        autoSelectSingle: !portfolioJustCleared,
                        filters: () => {
                          const equals: Record<string, string | number> = {};
                          if (bankName) equals.bank_name = bankName;
                          if (portfolioId) equals.id = portfolioId;
                          return { equals };
                        },
                      }}
                    />
                    <AutocompleteAsyncField
                      name="bank_name"
                      label={t("transactions.bank")}
                      control={form.control}
                      base_url={`${client}/${portfolio_table_name}`}
                      id_column={names.portfolioId}
                      column_name="bank_name"
                      showId={false}
                      isRequired
                      options={{
                        autoSelectSingle: !portfolioJustCleared,
                        filters: () => {
                          const equals: Record<string, string | number> = {};
                          if (clientName) equals.client = clientName;
                          if (portfolioId) equals.id = portfolioId;
                          return { equals };
                        },
                      }}
                    />
                  </div>
                ) : null}
                <AutocompleteAsyncField
                  name={names.portfolioName}
                  label={t("bank_positions.account")}
                  control={form.control}
                  base_url={`${client}/${portfolio_table_name}`}
                  id_column={names.portfolioId}
                  column_name="name"
                  isRequired
                  options={{
                    filters: () => {
                      const equals: Record<string, string> = {};
                      if (clientName) equals.client = clientName;
                      if (bankName) equals.bank_name = bankName;
                      return { equals };
                    },
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_value_local_ccy"
                    label={t("bank_positions.market_value_local_ccy")}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                  />
                  <InputField
                    name="market_value_ptf_ccy"
                    label={t("bank_positions.market_value_ptf_ccy")}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="opacity-50 text-xs">{reportingCurrency}</div>
                    }
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="fixing_local_ccy"
                    label={t("bank_positions.fixing_local_ccy")}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                    fieldOptions={{
                      isDisabled: !allowsFixingFields,
                      changeOnDisable: false,
                    }}
                  />
                  <InputField
                    name="fixing_ptf_ccy"
                    label={t("bank_positions.fixing_ptf_ccy")}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="opacity-50 text-xs">{reportingCurrency}</div>
                    }
                    fieldOptions={{
                      isDisabled: !allowsFixingFields,
                      changeOnDisable: false,
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="quantity"
                    label={t("bank_positions.quantity")}
                    control={form.control}
                    type="number"
                    fieldOptions={{
                      isDisabled: !allowsFixingFields,
                      changeOnDisable: false,
                    }}
                  />
                </div>
              </CardBody>
              <CardFooter>
                {id == null ? (
                  <FormActionsNew
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                  />
                ) : (
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={true}
                  />
                )}
              </CardFooter>
            </form>
          </Form>
        </Card>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t(id == null ? "confirm_create" : "confirm_update")}
          children={
            changedFields
              ? id == null
                ? NewDiff(
                    changedFields,
                    [names.portfolioId, names.staticDatumId],
                    undefined,
                    table_name,
                  )
                : Diff(
                    changedFields,
                    [names.portfolioId, names.staticDatumId],
                    undefined,
                    table_name,
                  )
              : t("no_changes")
          }
        />
        {id == null ? (
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={() => navigate(-1)}
          />
        ) : (
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
            requireTyping
          />
        )}
      </div>
    </div>
  );
}
